/**
 * Alpine component for the comment composer (top-level + replies).
 *
 * The editor is a `contenteditable` div (x-ref="editor"). @-mentions are inserted
 * as atomic, non-editable chips (`<span data-mention="ID">@Name</span>`). On submit
 * the DOM is serialized to a clean body string containing those mention spans —
 * `getMentionees()` parses them server-side, and CommonMark passes the span through
 * to the rendered comment (html_input=allow). It calls a Livewire method by name
 * (`$wire[method](...args, body)`) and NEVER stores the `$wire` proxy on its reactive
 * state (see CLAUDE.md): `$wire` is only touched inside method bodies invoked from
 * event handlers.
 *
 * Usage:
 *   x-data="commentComposer({ method: 'comment', args: [] })"          // top-level
 *   x-data="commentComposer({ method: 'reply',   args: [parentId] })"  // reply
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('commentComposer', (config = {}) => ({
        method: config.method,
        args: config.args ?? [],

        submitting: false,
        empty: true,

        // --- optional video timestamp anchor ---
        anchor: null,            // { provider, field_id, video_ref, seconds }
        anchorLabel: '',
        videoFields: [],         // [{ fieldId, label, provider, canCapture }] present on the page
        manualTime: '',          // mm:ss entry for non-scriptable providers (Google Drive)
        _onPlayersChanged: null,

        init() {
            this.refreshVideoFields();
            this._onPlayersChanged = () => this.refreshVideoFields();
            window.addEventListener('video-players:changed', this._onPlayersChanged);
        },

        destroy() {
            if (this._onPlayersChanged) {
                window.removeEventListener('video-players:changed', this._onPlayersChanged);
            }
        },

        mentionOpen: false,
        mentionQuery: '',
        matches: [],
        activeIndex: 0,
        searchTimer: null,
        searchRequestId: 0,

        // Fixed-position coordinates (viewport px) for the mention dropdown, which is
        // teleported to <body> so it can't be clipped by the request card's
        // `overflow-hidden`. Bound to the <ul> via :style.
        mentionTop: 0,
        mentionLeft: 0,
        mentionWidth: 0,

        // Non-reactive: where the active "@query" lives, so we can replace exactly
        // that range with a chip. Plain DOM refs — never rendered, never $wire.
        _mentionNode: null,
        _mentionStart: null,
        _mentionEnd: null,

        onInput() {
            this.updateEmpty();
            this.detectMention();
        },

        // ---- mention detection -------------------------------------------------

        detectMention() {
            const editor = this.$refs.editor;
            const sel = window.getSelection();
            if (!editor || !sel || !sel.isCollapsed || sel.rangeCount === 0) {
                return this.closeMention();
            }

            const node = sel.anchorNode;
            const offset = sel.anchorOffset;

            // Only inside a plain text node within the editor (not inside a chip).
            if (!node || node.nodeType !== Node.TEXT_NODE || !editor.contains(node)) {
                return this.closeMention();
            }

            const upto = node.textContent.slice(0, offset);
            const match = upto.match(/(?:^|\s)@([\w.\-]*)$/);

            if (match) {
                this._mentionNode = node;
                this._mentionStart = match.index + match[0].indexOf('@');
                this._mentionEnd = offset;
                this.mentionQuery = match[1];
                this.search();
            } else {
                this.closeMention();
            }
        },

        search() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.runSearch(), 150);
        },

        async runSearch() {
            const requestId = ++this.searchRequestId;

            let results;
            try {
                results = await this.$wire.searchMentions(this.mentionQuery);
            } catch (e) {
                // A failed lookup (e.g. authorize() 403, transient error) should just
                // close the menu, not bubble an unhandled rejection on every keystroke.
                if (requestId === this.searchRequestId) this.closeMention();
                return;
            }

            // Drop responses superseded by a newer search or a closed menu.
            if (requestId !== this.searchRequestId) return;

            this.matches = results ?? [];
            this.activeIndex = 0;

            if (this.matches.length > 0) {
                this.positionMention();
                this.mentionOpen = true;
            } else {
                this.mentionOpen = false;
            }
        },

        // Anchor the teleported dropdown to the editor: directly below it, flipping
        // above when there isn't room in the viewport. Recomputed on scroll/resize
        // while open (see the @scroll.window / @resize.window bindings on the <ul>).
        positionMention() {
            const editor = this.$refs.editor;
            if (!editor) return;

            const rect = editor.getBoundingClientRect();
            const gap = 6;
            // Actual height once rendered, else an estimate (row ≈ 44px, +8 padding, capped at max-h-56).
            const estimated = Math.min(this.matches.length * 44 + 8, 224);
            const height = this.$refs.mentionList?.offsetHeight || estimated;
            const spaceBelow = window.innerHeight - rect.bottom;

            this.mentionLeft = rect.left;
            this.mentionWidth = rect.width;
            this.mentionTop = (spaceBelow < height + gap && rect.top > spaceBelow)
                ? rect.top - height - gap   // flip above
                : rect.bottom + gap;        // default: below
        },

        moveDown() {
            if (this.mentionOpen) {
                this.activeIndex = Math.min(this.activeIndex + 1, this.matches.length - 1);
            }
        },

        moveUp() {
            if (this.mentionOpen) {
                this.activeIndex = Math.max(this.activeIndex - 1, 0);
            }
        },

        closeMention() {
            clearTimeout(this.searchTimer);
            this.searchRequestId++;
            this.mentionOpen = false;
            this.mentionQuery = '';
            this.matches = [];
            this._mentionNode = null;
            this._mentionStart = null;
            this._mentionEnd = null;
        },

        // ---- chip insertion ----------------------------------------------------

        buildChip(match) {
            const chip = document.createElement('span');
            chip.dataset.mention = String(match.id);
            chip.setAttribute('contenteditable', 'false');
            chip.className = 'comment-mention';
            chip.textContent = '@' + (match.name || match.email);

            return chip;
        },

        insertMention(match) {
            const chosen = match ?? this.matches[this.activeIndex];
            if (!chosen) return;

            const editor = this.$refs.editor;
            const range = document.createRange();

            if (this._mentionNode && editor.contains(this._mentionNode)) {
                range.setStart(this._mentionNode, this._mentionStart);
                range.setEnd(this._mentionNode, this._mentionEnd);
            } else {
                range.selectNodeContents(editor);
                range.collapse(false);
            }
            range.deleteContents();

            const chip = this.buildChip(chosen);
            const space = document.createTextNode(' ');
            range.insertNode(space);
            range.insertNode(chip); // inserts before space → [chip][space]

            // Caret right after the trailing space.
            const after = document.createRange();
            after.setStart(space, 1);
            after.collapse(true);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(after);

            editor.focus();
            this.closeMention();
            this.updateEmpty();
        },

        // ---- keyboard ----------------------------------------------------------

        onEnter(e) {
            // Cmd/Ctrl+Enter is the submit shortcut (its own handlers) — don't also
            // treat it as a newline here.
            if (e.metaKey || e.ctrlKey) return;

            if (this.mentionOpen) {
                this.insertMention();
                e.preventDefault();
                return;
            }
            // Dropdown closed: insert a deterministic <br> rather than letting the
            // browser create <div>/<p> wrappers. serialize() maps <br> → "\n".
            e.preventDefault();
            this.insertBreak();
        },

        insertBreak() {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;

            const range = sel.getRangeAt(0);
            range.deleteContents();
            const br = document.createElement('br');
            range.insertNode(br);

            const after = document.createRange();
            after.setStartAfter(br);
            after.collapse(true);
            sel.removeAllRanges();
            sel.addRange(after);

            this.updateEmpty();
        },

        // ---- paste (force plain text) ------------------------------------------

        onPaste(e) {
            const text = e.clipboardData?.getData('text/plain');
            if (!text) return;

            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;

            const range = sel.getRangeAt(0);
            range.deleteContents();

            const frag = document.createDocumentFragment();
            text.split('\n').forEach((part, i) => {
                if (i > 0) frag.appendChild(document.createElement('br'));
                frag.appendChild(document.createTextNode(part));
            });
            const lastNode = frag.lastChild;
            range.insertNode(frag);

            if (lastNode) {
                const after = document.createRange();
                after.setStartAfter(lastNode);
                after.collapse(true);
                sel.removeAllRanges();
                sel.addRange(after);
            }

            this.updateEmpty();
            this.detectMention();
        },

        // ---- serialization -----------------------------------------------------

        escapeHtml(value) {
            return value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },

        serialize(el) {
            let out = '';

            const walk = (node) => {
                node.childNodes.forEach((child) => {
                    if (child.nodeType === Node.TEXT_NODE) {
                        // Escape plain text the same way mention labels are escaped —
                        // serialize() emits HTML (the mention spans), so raw user text
                        // must be encoded here rather than relying solely on the
                        // server-side sanitizer.
                        out += this.escapeHtml(child.textContent);
                        return;
                    }
                    if (child.nodeType !== Node.ELEMENT_NODE) return;

                    const tag = child.tagName;

                    if (tag === 'SPAN' && child.dataset.mention) {
                        const id = child.dataset.mention;
                        const label = this.escapeHtml(child.textContent); // "@Name"
                        out += `<span data-mention="${id}">${label}</span>`;
                        return;
                    }

                    if (tag === 'BR') {
                        out += '\n';
                        return;
                    }

                    if (tag === 'DIV' || tag === 'P') {
                        if (out !== '' && !out.endsWith('\n')) out += '\n';
                        walk(child);
                        if (!out.endsWith('\n')) out += '\n';
                        return;
                    }

                    walk(child);
                });
            };

            walk(el);

            return out.replace(/\n+$/, '');
        },

        updateEmpty() {
            const editor = this.$refs.editor;
            if (!editor) return;

            // Keep the empty state pristine so the :empty placeholder reappears
            // (browsers can leave a stray <br> after the last char is deleted).
            if (editor.textContent.trim() === '' && !editor.querySelector('[data-mention]')) {
                if (editor.innerHTML !== '') editor.innerHTML = '';
            }

            this.empty = this.serialize(editor).trim().length === 0;
        },

        clear() {
            const editor = this.$refs.editor;
            if (editor) editor.innerHTML = '';
            this.updateEmpty();
        },

        // ---- video timestamp anchor -------------------------------------------

        refreshVideoFields() {
            const list = (window.VideoPlayers && window.VideoPlayers.list()) || [];
            this.videoFields = list.map((handle) => ({
                fieldId: handle.fieldId,
                label: handle.label,
                provider: handle.provider,
                canCapture: handle.canCapture,
            }));

            // Drop the anchor if its field is no longer on the page.
            if (this.anchor && !this.videoFields.some((field) => field.fieldId === this.anchor.field_id)) {
                this.clearAnchor();
            }
        },

        async captureTimestamp(fieldId) {
            const handle = window.VideoPlayers && window.VideoPlayers.get(fieldId);
            if (!handle) {
                console.warn('[video] capture: no registered player for field', fieldId);
                return;
            }

            const seconds = await handle.getCurrentTime();
            console.log('[video] capture: resolved', seconds, 'for field', fieldId, '(provider', handle.provider + ')');
            if (seconds === null || seconds === undefined) return;

            this.setAnchor(handle, Math.max(0, Math.floor(seconds)));
        },

        setManualAnchor(fieldId) {
            const handle = window.VideoPlayers && window.VideoPlayers.get(fieldId);
            if (!handle) return;

            const seconds = this.parseManualTime(this.manualTime);
            if (seconds === null) return;

            this.setAnchor(handle, seconds);
            this.manualTime = '';
        },

        setAnchor(handle, seconds) {
            this.anchor = {
                provider: handle.provider,
                field_id: handle.fieldId,
                video_ref: handle.videoRef ?? null,
                seconds,
            };
            this.anchorLabel = this.formatSeconds(seconds);
        },

        clearAnchor() {
            this.anchor = null;
            this.anchorLabel = '';
        },

        parseManualTime(value) {
            if (!value) return null;
            value = String(value).trim();
            if (/^\d+$/.test(value)) return parseInt(value, 10);
            if (!/^\d{1,2}(:\d{1,2}){1,2}$/.test(value)) return null;

            const parts = value.split(':').map((part) => parseInt(part, 10));

            // Every component after the leading one (minutes / seconds) must be
            // 0–59; reject "1:90" or "99:99" instead of miscomputing the total.
            if (parts.slice(1).some((part) => part < 0 || part > 59)) return null;

            return parts.reduce((acc, part) => acc * 60 + part, 0);
        },

        formatSeconds(total) {
            total = Math.max(0, Math.floor(total));
            const hours = Math.floor(total / 3600);
            const minutes = Math.floor((total % 3600) / 60);
            const seconds = total % 60;
            const pad = (n) => String(n).padStart(2, '0');

            return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
        },

        // ---- submit ------------------------------------------------------------

        async submit() {
            if (this.mentionOpen) {
                this.insertMention();
                return;
            }

            const body = this.serialize(this.$refs.editor).trim();
            if (!body || body.length > 10000 || this.submitting) return;

            this.submitting = true;
            try {
                const args = this.anchor
                    ? [...this.args, body, this.anchor]
                    : [...this.args, body];
                await this.$wire[this.method](...args);
                this.clear();
                this.clearAnchor();
                this.closeMention();
                this.$dispatch('comment-posted');
            } finally {
                this.submitting = false;
            }
        },
    }));
});
